import { Box, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";

import { useIdentity } from "../../../IdentityProvider";
import { useTrueBizApi } from "../../../../api";

const IMAGE_READY_STATUS = 200;

export function SocialMediaImage({
  src,
  relayQueryParameters = null,
  relayRequestId = null,
}: {
  src: string;
  relayQueryParameters?: URLSearchParams | null;
  relayRequestId?: string | null;
}) {
  const api = useTrueBizApi();

  const { isAuthenticated } = useIdentity();
  const anonymousAccessAllowed =
    !!relayQueryParameters &&
    Array.from(relayQueryParameters.keys()).length > 0;
  const hasAuthComponents = isAuthenticated || anonymousAccessAllowed;

  const [imgObjectUrl, setImageObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<boolean>(false);

  useEffect(() => {
    setFailed(false);
    setImageObjectUrl(null);
  }, [src, relayQueryParameters]);

  useEffect(() => {
    if (!hasAuthComponents) return;
    if (!src) return;

    const fetchImage = async (
      src: string,
      abortController: AbortController
    ) => {
      try {
        const result = isAuthenticated
          ? await api.getSocialMediaPostImage(src, abortController.signal)
          : window.location.href.includes("/passfort/")
            ? await api.getPassfortSocialMediaPostImage(
                src,
                relayQueryParameters,
                relayRequestId,
                abortController.signal
              )
            : await api.getRelaySocialMediaPostImage(
                src,
                relayQueryParameters,
                relayRequestId,
                abortController.signal
              );

        if (result.status === IMAGE_READY_STATUS && result.url) {
          setImageObjectUrl(result.url);
        } else {
          throw new Error(
            `Social media image request failed with status ${result.status}`
          );
        }
      } catch (e) {
        if (
          e instanceof DOMException &&
          e.name === "AbortError" &&
          controller.signal.aborted
        ) {
          // ignore any aborted pending requests
          return;
        }

        console.error(e);
        setFailed(true);
      }
    };

    const controller = new AbortController();
    fetchImage(src, controller);

    return () => controller.abort();
  }, [
    isAuthenticated,
    hasAuthComponents,
    api,
    src,
    relayQueryParameters,
    relayRequestId,
  ]);

  if (!hasAuthComponents) return null;

  if (failed)
    return (
      <Box
        sx={{
          textAlign: "center",
          p: 2,
        }}
      >
        Could not load image
      </Box>
    );

  return (
    <Box>
      {imgObjectUrl ? (
        <Box>
          <img
            style={{ width: "100%", display: "block" }}
            src={imgObjectUrl}
            alt=""
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            p: 4,
          }}
        >
          <CircularProgress color="inherit" />

          <Box
            sx={{
              marginTop: 3,
            }}
          >
            Loading image...
          </Box>
        </Box>
      )}
    </Box>
  );
}
